import { isBarePiece, type LookSlot } from "../rules/look-records.ts";
import type { WireLook } from "../protocol/looks.ts";
import { fallbackNumber, lookByNumber, lookNumber, type Looks } from "../rules/looks.ts";
import type { LookComposeLayer, LookLayout } from "./look-layout.ts";
import { STARTER_WORN } from "./look.ts";
import { DIRECTION_NAMES, REQUIRED_ANIMS, type DirectionName } from "./sheet.ts";

/**
 * The fallback look (ADR-094 decision 15) and the one-layer character sheet the build bakes from it (decision 33).
 *
 * The fallback look is a whole character: the first skin, the first clothes and the first hair of the looks table,
 * with the starter tools. A page draws it for anyone whose own look is not ready, and the build composes it into a
 * sheet in the character sheet format (ADR-036) for whoever draws a player without the look cache: the editor's
 * stand-in.
 */

/**
 * The name of the baked sheet in the character manifest.
 */
export const BAKED_SHEET = "fallback-look";

/**
 * The strips the baked sheet holds: the ones a character sheet must have.
 */
export const BAKED_STRIPS: readonly string[] = REQUIRED_ANIMS;

/**
 * The fallback look, which anyone whose look is not ready wears: the first skin, the first clothes, the first hair
 * of the table and the starter tools. It is a whole character, unlike a slot's fallback (`fallbackNumber`, decision
 * 10), which is no hair and empty hands.
 *
 * @param looks The looks table.
 * @param out Where the look is written.
 * @returns `out`.
 */
export function fallbackLook(looks: Looks, out: WireLook): WireLook {
  out.skin = fallbackNumber(looks, "skin");
  out.outfit = fallbackNumber(looks, "outfit");
  out.hair = looks.pieces.findIndex((piece) => piece.slot === "hair") + 1;
  out.hat = 0;
  out.mainHand = STARTER_WORN.mainHand === null ? 0 : (lookNumber(looks, STARTER_WORN.mainHand) ?? 0);
  out.offHand = STARTER_WORN.offHand === null ? 0 : (lookNumber(looks, STARTER_WORN.offHand) ?? 0);
  out.flags = 0;
  return out;
}

/**
 * The piece sheets the fallback look is composed from, in the drawing order: a body layer's sheet is its piece's
 * key, a tool's the key with the layer's rank.
 *
 * @param looks The looks table.
 * @param compose The drawing order, bottom first.
 * @returns The sheet ids, bottom first; a slot the fallback look leaves empty, or fills with a bare piece, has none.
 */
export function fallbackSheetIds(looks: Looks, compose: readonly LookComposeLayer[]): string[] {
  const look = fallbackLook(looks, { skin: 0, hair: 0, flags: 0, outfit: 0, hat: 0, mainHand: 0, offHand: 0 });
  const ids: string[] = [];
  for (const layer of compose) {
    const slot: LookSlot = layer.slot;
    const piece = look[slot] === 0 ? undefined : lookByNumber(looks, look[slot]);
    if (piece !== undefined && !isBarePiece(piece)) {
      ids.push(layer.rank === null ? piece.key : `${piece.key}.${layer.rank}`);
    }
  }
  return ids;
}

/**
 * How the baked sheet is cut: the one frame size every strip shares, the pivot's place in it, and for each strip its
 * rate, its length and the layout cell of each frame in each direction.
 */
export interface BakedSheetPlan {
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly pivot: { readonly x: number; readonly y: number };
  readonly anims: Readonly<Record<string, { readonly fps: number; readonly frames: number; readonly cells: Readonly<Record<DirectionName, readonly number[]>> }>>;
}

/**
 * Plans the sheet baked from a look layout: a character sheet has one frame size and one pivot (ADR-036), where a
 * layout's cells are each trimmed, so the frame is the box around every baked cell as it stands on the pivot, and
 * the pivot's place in that box is the sheet's pivot. The box always holds the pivot. A strip is as long as its
 * shortest direction.
 *
 * @param layout The look layout.
 * @param strips The strips to bake, by name.
 * @returns The plan.
 * @throws Error when the layout lacks a strip, a strip has no frame in a direction, or a frame names no cell.
 */
export function bakedSheetPlan(layout: LookLayout, strips: readonly string[]): BakedSheetPlan {
  let left = 0;
  let top = 0;
  let right = 0;
  let bottom = 0;
  const anims: Record<string, { fps: number; frames: number; cells: Record<DirectionName, readonly number[]> }> = {};
  for (const name of strips) {
    const strip = layout.strips[name];
    if (strip === undefined) {
      throw new Error(`the look layout has no strip ${name}`);
    }
    let frames = Number.POSITIVE_INFINITY;
    for (const direction of DIRECTION_NAMES) {
      const count = strip.frames[direction].length;
      if (count === 0) {
        throw new Error(`the look layout's strip ${name} has no frame facing ${direction}`);
      }
      frames = Math.min(frames, count);
    }
    const cells = {} as Record<DirectionName, readonly number[]>;
    for (const direction of DIRECTION_NAMES) {
      const cut = strip.frames[direction].slice(0, frames);
      for (const index of cut) {
        const cell = layout.cells[index];
        if (cell === undefined) {
          throw new Error(`the look layout's strip ${name} names cell ${index}, which the layout lacks`);
        }
        left = Math.min(left, cell.offsetX);
        top = Math.min(top, cell.offsetY);
        right = Math.max(right, cell.offsetX + cell.width);
        bottom = Math.max(bottom, cell.offsetY + cell.height);
      }
      cells[direction] = cut;
    }
    anims[name] = { fps: strip.fps, frames, cells };
  }
  return { frameWidth: right - left, frameHeight: bottom - top, pivot: { x: 0 - left, y: 0 - top }, anims };
}
